import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { RootState } from 'src/redux/rootReducerType';
import type { AppDispatch } from 'src/redux/store';
import { Certification } from 'src/types/Certification';
import type { CertStatusActionId } from 'src/types/CertStatus';
import { RENTER_PATHS, buildBookingPath } from 'src/routes/renterPaths';
import {
  fetchRenterBookingOptions,
  useRenterAppointments,
} from '../Appointments/useRenterAppointments';
import { fetchContinueCertification } from 'src/services/CertificationContinue.service';
import {
  clearAllDeferred,
  deferFileUpload,
} from 'src/redux/slices/Interview/interviewSlice';
import {
  fetchFileQuestions,
  selectFileQuestions,
} from 'src/redux/slices/Survey/surveyFileQuestionsSlice';
import { setCertStatus } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import { userService } from 'src/services/User.service';
import PageHeader from '../PageHeader/PageHeader';
import { fadeStyle } from 'src/components/NewRenterInterface/constants';
import {
  getMemberSurvey,
  certStatusState,
  memberNeedsDocumentsFromStatus,
} from '../Household/householdSurveyUtils';
import CertificationActionRow from './CertificationActionRow';
import HubFileTile from './HubFileTile';
import type { HubFile } from './HubFileTile';
import FilePreviewModal from './FilePreviewModal';
import OnBehalfOfBanner from '../shared/OnBehalfOfBanner';
import { certKindLabel } from '../utils/certTerminology';
import CertStatusDetail from '../CertStatus/CertStatusDetail';
import { certStatusActionNavigation } from '../CertStatus/certStatusActions';

const CertificationHubPage: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const { certificationId, userId } = useParams<{
    certificationId: string;
    userId: string;
  }>();

  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[];

  const certStatusLoading = useSelector(
    (state: RootState) => state.newRenterInterface.certStatusLoading
  );

  const certStatus = useSelector(
    (state: RootState) => state.newRenterInterface.certStatus ?? {}
  );

  const cert = useMemo(
    () =>
      (certifications || []).find((c) => c.id === Number(certificationId)) ??
      null,
    [certifications, certificationId]
  );

  const certId = cert?.id ?? null;

  // Fetch this cert's status (IVR status text + action items) on page load so
  // the Status section is fresh rather than relying only on the login batch.
  useEffect(() => {
    if (certId == null) {
      return;
    }
    let cancelled = false;
    userService
      .fetchCertStatus([certId])
      .then((data) => {
        if (!cancelled) {
          dispatch(setCertStatus(data ?? {}));
        }
      })
      .catch(() => {
        if (!cancelled) {
          dispatch(setCertStatus({}));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [certId, dispatch]);

  const targetUserId = Number(userId);
  const member = useMemo(
    () =>
      cert?.cert_household?.cert_members?.find(
        (m) => m.user_id === targetUserId
      ) ?? null,
    [cert, targetUserId]
  );
  const surveyId =
    cert && member ? getMemberSurvey(cert, member.cert_member_id) : null;
  const isOnBehalf = user != null && targetUserId !== user.id;

  const onBehalfName = useMemo(
    () =>
      isOnBehalf && member ? `${member.first_name} ${member.last_name}` : null,
    [isOnBehalf, member]
  );

  const statusDetail = certStatus[cert?.id ?? -1];
  const statusLoaded = statusDetail !== undefined;
  const showActionsLoading = certStatusLoading && !statusLoaded;
  const needsAction =
    statusLoaded &&
    certStatusState(statusDetail, targetUserId, member?.cert_member_id) ===
      'action';
  const isCompleted =
    !showActionsLoading &&
    (cert?.status?.renter_active === false || (statusLoaded && !needsAction));
  // Document work (rejected/insufficient files) has its own "Upload more
  // documents" row. "Continue interview" is always offered while the cert has
  // outstanding actions: the interview itself presents the file questions
  // (rejected / insufficient uploads), so gating it out for document-only
  // actions hid a valid path to resolve them.
  const needsDocuments =
    statusLoaded &&
    memberNeedsDocumentsFromStatus(statusDetail, member?.cert_member_id);
  const showUploadRow = !statusLoaded || needsDocuments;
  const hasActionRows = !showActionsLoading && !isCompleted;

  // Survey (questionnaire) files for the member — same source as My Documents.
  const actingOnMemberId = isOnBehalf ? targetUserId : null;
  const fileQuestionsEntry = useSelector(
    selectFileQuestions(surveyId ?? 0, actingOnMemberId)
  );

  useEffect(() => {
    if (surveyId == null) {
      return;
    }
    dispatch(fetchFileQuestions({ surveyId, actingOnMemberId }));
  }, [dispatch, surveyId, actingOnMemberId]);

  const filesLoading =
    !fileQuestionsEntry || fileQuestionsEntry.status === 'loading';

  // Show the My Documents section when cards are loaded (even zero cards means
  // visible file questions exist), or when the fetch failed — so Manage
  // documents is always reachable and never silently disappears.
  const showDocsSection =
    (fileQuestionsEntry?.cards ?? []).length > 0 ||
    fileQuestionsEntry?.status === 'failed';

  const recentFiles = useMemo<HubFile[]>(() => {
    const cards = fileQuestionsEntry?.cards ?? [];
    return cards
      .flatMap((card) =>
        card.questions.flatMap((q) =>
          q.files.map((file) => ({ ...file, qitId: q.qit_id }))
        )
      )
      .sort((a, b) => {
        // Rejected files first, then most recent.
        const rejectedDelta =
          Number(b.is_rejected ?? false) - Number(a.is_rejected ?? false);
        if (rejectedDelta !== 0) {
          return rejectedDelta;
        }
        return (
          new Date(b.created_at ?? 0).getTime() -
          new Date(a.created_at ?? 0).getTime()
        );
      })
      .slice(0, 4);
  }, [fileQuestionsEntry]);

  // Defensive: RenterResourceGuard already 403s foreign certs; this covers
  // a member with no resolvable survey (nothing to act on).
  useEffect(() => {
    if (cert && (!member || surveyId == null)) {
      navigate(`/${RENTER_PATHS.HOME}`, { replace: true });
    }
  }, [cert, member, surveyId, navigate]);

  const [continuing, setContinuing] = useState(false);
  const [previewFile, setPreviewFile] = useState<HubFile | null>(null);

  const filesPath = `/${RENTER_PATHS.HOME}/certification/${Number(
    certificationId
  )}/files/${targetUserId}`;

  const handleUploadDocuments = useCallback(() => {
    navigate(filesPath);
  }, [navigate, filesPath]);

  const handleManageFiles = useCallback(() => {
    if (surveyId == null) {
      return;
    }
    navigate(
      isOnBehalf
        ? `/${RENTER_PATHS.HOME}/survey/${surveyId}/members/${targetUserId}/my-documents`
        : `/${RENTER_PATHS.HOME}/survey/${surveyId}/my-documents`
    );
  }, [surveyId, navigate, isOnBehalf, targetUserId]);

  // Rejected tiles deep-link into the My Documents edit (re-upload) flow —
  // the same route MyDocumentsPage uses when a question row is tapped.
  const handleFileTileClick = useCallback(
    (file: HubFile) => {
      if (file.is_rejected && surveyId != null) {
        const base = isOnBehalf
          ? `/${RENTER_PATHS.HOME}/survey/${surveyId}/members/${targetUserId}/my-documents/answer`
          : `/${RENTER_PATHS.HOME}/survey/${surveyId}/my-documents/answer`;
        navigate(`${base}?qits=${file.qitId ?? ''}`);
        return;
      }
      setPreviewFile(file);
    },
    [navigate, isOnBehalf, surveyId, targetUserId]
  );

  const handleContinueInterview = useCallback(async () => {
    if (surveyId == null || continuing) {
      return;
    }
    setContinuing(true);
    try {
      const { redirect_to: redirectTo } = await fetchContinueCertification(
        surveyId
      );
      dispatch(clearAllDeferred());
      dispatch(deferFileUpload());
      const base = `/${RENTER_PATHS.HOME}/interview/${surveyId}`;
      if (redirectTo === 'data-transfer') {
        navigate(
          isOnBehalf
            ? `${base}/data-transfer/members/${targetUserId}`
            : `${base}/data-transfer`
        );
      } else {
        navigate(isOnBehalf ? `${base}/members/${targetUserId}` : base);
      }
    } finally {
      setContinuing(false);
    }
  }, [surveyId, continuing, dispatch, navigate, isOnBehalf, targetUserId]);

  const handleStatusAction = useCallback(
    (id: CertStatusActionId) => {
      if (surveyId == null) {
        return;
      }
      const { path, state } = certStatusActionNavigation(
        id,
        surveyId,
        isOnBehalf ? targetUserId : undefined
      );
      if (path == null) {
        return;
      }
      if (state) {
        navigate(path, { state });
      } else {
        navigate(path);
      }
    },
    [surveyId, isOnBehalf, targetUserId, navigate]
  );

  // ENG-3895: "Schedule an appointment" row — only when this cert's property
  // allows online booking. Shows the booked time when one exists.
  const [bookable, setBookable] = useState(false);
  const { upcoming: upcomingAppointments } = useRenterAppointments();
  useEffect(() => {
    let cancelled = false;
    if (certId == null) {
      return undefined;
    }
    fetchRenterBookingOptions(certId)
      .then((opts) => {
        if (!cancelled) {
          setBookable(Boolean(opts?.bookable));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBookable(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [certId]);
  const certAppointment = useMemo(
    () => upcomingAppointments.find((a) => a.certification === certId) ?? null,
    [upcomingAppointments, certId]
  );
  const handleAppointmentRow = useCallback(() => {
    if (certId == null) {
      return;
    }
    navigate(
      certAppointment
        ? `/${RENTER_PATHS.HOME}/${RENTER_PATHS.APPOINTMENTS}`
        : buildBookingPath(certId)
    );
  }, [certId, certAppointment, navigate]);

  const handleQuestionnaireSummary = useCallback(() => {
    if (surveyId == null) {
      return;
    }
    dispatch(clearAllDeferred());
    navigate(
      isOnBehalf
        ? `/${RENTER_PATHS.HOME}/survey/${surveyId}/members/${targetUserId}`
        : `/${RENTER_PATHS.HOME}/survey/${surveyId}`
    );
  }, [surveyId, dispatch, navigate, isOnBehalf, targetUserId]);

  if (!cert || !member || surveyId == null) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100%', bgcolor: 'background.neutral' }}>
      <PageHeader
        title={cert.property?.name ?? ''}
        subtitle={`Complete your ${certKindLabel(
          cert
        )}, check your data or manage your files`}
        backRoute={`/${RENTER_PATHS.HOME}`}
      />
      <OnBehalfOfBanner name={onBehalfName} />
      <Stack sx={{ p: 3, gap: 2.5, maxWidth: 480, mx: 'auto', ...fadeStyle }}>
        <Box>
          <Typography sx={{ fontSize: 17, fontWeight: 600, mb: 1.5 }}>
            Status
          </Typography>
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '16px',
              boxShadow: '0px 2px 10px rgba(0,0,0,0.06)',
              p: 2.5,
            }}
          >
            <CertStatusDetail
              detail={statusDetail}
              selectedUserId={targetUserId}
              selectedCertMemberId={member.cert_member_id}
              loading={!statusDetail}
              onAction={handleStatusAction}
            />
          </Box>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 17, fontWeight: 600, mb: 1.5 }}>
            {cert.category?.name}
          </Typography>
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '16px',
              boxShadow: '0px 2px 10px rgba(0,0,0,0.06)',
            }}
          >
            {showActionsLoading && (
              <Box sx={{ p: 2 }}>
                <Skeleton variant="rounded" height={48} animation="wave" />
                <Skeleton
                  variant="rounded"
                  height={48}
                  animation="wave"
                  sx={{ mt: 1.5 }}
                />
                <Skeleton
                  variant="rounded"
                  height={48}
                  animation="wave"
                  sx={{ mt: 1.5 }}
                />
              </Box>
            )}
            {!showActionsLoading && isCompleted && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  p: 2.5,
                }}
              >
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 28, color: 'success.main', mt: 0.25 }}
                />
                <Stack gap={0.5}>
                  <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                    Interview Complete
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.5 }}
                  >
                    Your {certKindLabel(cert)} has been submitted. A compliance
                    specialist will review it and contact you via email or SMS
                    if anything else is needed.
                  </Typography>
                </Stack>
              </Box>
            )}
            {!showActionsLoading && !isCompleted && (
              <>
                {showUploadRow && (
                  <CertificationActionRow
                    icon={
                      <UploadFileOutlinedIcon
                        sx={{ fontSize: 18, color: 'info.dark' }}
                      />
                    }
                    iconBgColor={alpha(theme.palette.info.main, 0.12)}
                    label="Upload more documents"
                    onClick={handleUploadDocuments}
                  />
                )}
                {showUploadRow && <Divider sx={{ ml: 6.5 }} />}
                <CertificationActionRow
                  icon={
                    <AssignmentOutlinedIcon
                      sx={{ fontSize: 18, color: 'warning.dark' }}
                    />
                  }
                  iconBgColor={alpha(theme.palette.warning.main, 0.12)}
                  label="Continue interview"
                  onClick={handleContinueInterview}
                  loading={continuing}
                />
              </>
            )}
            {!showActionsLoading && (
              <>
                {hasActionRows && <Divider sx={{ ml: 6.5 }} />}
                <CertificationActionRow
                  icon={
                    <EditOutlinedIcon
                      sx={{ fontSize: 18, color: 'info.dark' }}
                    />
                  }
                  iconBgColor={alpha(theme.palette.info.main, 0.12)}
                  label="Interview summary"
                  onClick={handleQuestionnaireSummary}
                />
                {(bookable || certAppointment) && (
                  <>
                    <Divider sx={{ ml: 6.5 }} />
                    <CertificationActionRow
                      icon={
                        <EventAvailableOutlinedIcon
                          sx={{ fontSize: 18, color: 'success.dark' }}
                        />
                      }
                      iconBgColor={alpha(theme.palette.success.main, 0.12)}
                      label={
                        certAppointment
                          ? `Your appointment — ${certAppointment.start_local}`
                          : 'Schedule an appointment'
                      }
                      onClick={handleAppointmentRow}
                    />
                  </>
                )}
              </>
            )}
          </Box>
        </Box>
        {/* Show the My Documents section whenever there are visible file
            questions — even with zero uploads, so renters can reach the
            empty-slot upload flow. Tiles only appear once files exist. */}
        {showDocsSection && (
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 600, mb: 1.5 }}>
              My Documents
            </Typography>
            {recentFiles.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 1.25,
                  mb: 1.5,
                }}
              >
                {recentFiles.map((file) => (
                  <HubFileTile
                    key={file.id}
                    file={file}
                    onClick={handleFileTileClick}
                  />
                ))}
              </Box>
            )}
            <Stack
              direction="row"
              role="button"
              tabIndex={0}
              aria-label="Manage documents"
              onClick={handleManageFiles}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleManageFiles();
                }
              }}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: '12px',
                px: 2,
                py: 1.5,
                cursor: 'pointer',
              }}
            >
              <Typography
                sx={{ fontSize: 14, fontWeight: 700, color: 'primary.main' }}
              >
                Manage documents
              </Typography>
              <ChevronRightIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            </Stack>
          </Box>
        )}
        {!showDocsSection && filesLoading && (
          <Box>
            <Skeleton
              variant="text"
              width={80}
              height={24}
              animation="wave"
              sx={{ mb: 1.5 }}
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 1.25,
                mb: 1.5,
              }}
            >
              {[0, 1, 2, 3].map((i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={112}
                  animation="wave"
                  sx={{ borderRadius: '12px' }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Stack>
      <FilePreviewModal
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </Box>
  );
};

export default CertificationHubPage;
